import { useNavigate } from "react-router-dom";
import { formatDateFull, formatCurrency, daysUntil } from "../../utils/format";
import { Pencil, Trash2, Bell, FileText, CheckCircle2 } from "lucide-react";

function rowAccent(w) {
  if (w.status_key === "cancelled") return "border-l-zinc-300 opacity-50";
  if (w.status_key === "realized") return "border-l-emerald-500";
  const d = daysUntil(w.date);
  if (d === 0) return "border-l-orange-500 bg-orange-50/40";
  if (d !== null && d > 0 && d <= 4) return "border-l-amber-400 bg-amber-50/30";
  return "border-l-transparent";
}

export default function WorkshopTable({ workshops, onDelete, onNotify, onInvoice, canEdit }) {
  const navigate = useNavigate();

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="workshop-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
              <th className="text-left font-medium px-2 py-2">Den</th>
              <th className="text-left font-medium px-2 py-2">Škola</th>
              <th className="text-left font-medium px-2 py-2">PSČ</th>
              <th className="text-left font-medium px-2 py-2">Okres</th>
              <th className="text-left font-medium px-2 py-2">Program</th>
              <th className="text-center font-medium px-2 py-2">WS</th>
              <th className="text-left font-medium px-2 py-2">Lektoři</th>
              <th className="text-right font-medium px-2 py-2">Cena</th>
              <th className="text-left font-medium px-2 py-2">Faktura</th>
              <th className="text-right font-medium px-2 py-2">Akce</th>
            </tr>
          </thead>
          <tbody>
            {workshops.map((w) => (
              <tr
                key={w.id}
                data-testid={`workshop-row-${w.id}`}
                onClick={() => canEdit && navigate(`/workshop/edit/${w.id}`)}
                className={`border-l-2 border-b border-border/60 hover:bg-secondary/40 transition-colors ${canEdit ? "cursor-pointer" : ""} ${rowAccent(w)}`}
              >
                <td className="px-2 py-1.5 whitespace-nowrap font-medium">{formatDateFull(w.date)}</td>
                <td className="px-2 py-1.5">
                  <div className="font-medium truncate max-w-[180px]">{w.school_name}</div>
                  <div className="text-[11px] text-muted-foreground">{w.school_city}</div>
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">{w.school_zip}</td>
                <td className="px-2 py-1.5 truncate max-w-[110px] text-muted-foreground">{w.school_district}</td>
                <td className="px-2 py-1.5 truncate max-w-[130px]">{w.program_name}</td>
                <td className="px-2 py-1.5 text-center tabular-nums">
                  {(w.items || []).reduce((a, i) => a + (i.count || 0), 0)}
                </td>
                <td className="px-2 py-1.5 truncate max-w-[120px]">{(w.lector_names || []).join(", ")}</td>
                <td className="px-2 py-1.5 text-right tabular-nums font-medium whitespace-nowrap">{formatCurrency(w.revenue)}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {w.invoice?.number ? (
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] ${w.invoice.paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                      {w.invoice.paid && <CheckCircle2 className="h-3 w-3" />}
                      {w.invoice.number}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-[11px]">—</span>
                  )}
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {canEdit && (
                      <button data-testid={`ws-edit-${w.id}`} onClick={() => navigate(`/workshop/edit/${w.id}`)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground" title="Upravit">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {onNotify && (
                      <button data-testid={`ws-notify-${w.id}`} onClick={() => onNotify(w)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground" title="Notifikovat lektory">
                        <Bell className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {onInvoice && (
                      <button data-testid={`ws-invoice-${w.id}`} onClick={() => onInvoice(w)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground" title="Fakturovat">
                        <FileText className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {canEdit && (
                      <button data-testid={`ws-delete-${w.id}`} onClick={() => onDelete(w)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Smazat">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {workshops.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-12 text-center text-muted-foreground">Žádné workshopy</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
